import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search, Menu } from 'lucide-react';
import { RiSparkling } from '@/components/icons/remix';
import { useUI } from '@/store/ui';
import { Avatar } from '@/components/Avatar';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/DropdownMenu';
import { NotificationsPopover } from '@/components/NotificationsPopover';
import { CopilotPanel } from '@/components/CopilotPanel';
import { logout, me } from '@/lib/auth';

/**
 * Persistent top chrome — a slim utility strip. Navigation + search live in
 * the rail and page context lives in each page's own header, so the bar only
 * carries the right-aligned utilities (copilot / notifications / account) plus
 * the mobile nav + search triggers. (The page-context breadcrumb was removed —
 * it duplicated the in-page PageHeader breadcrumb.)
 */
export function TopChrome() {
  const navigate = useNavigate();
  const togglePalette = useUI((s) => s.togglePalette);
  const toggleMobileNav = useUI((s) => s.toggleMobileNav);
  // Copilot panel state lives in the store so other components (the nudge
  // bar) can open it with a seed prompt. The button toggles between the
  // floating card and fully hidden; chip-minimization is its own state
  // surfaced from the panel itself.
  const copilotOpen      = useUI((s) => s.copilotOpen);
  const copilotMinimized = useUI((s) => s.copilotMinimized);
  const openCopilot      = useUI((s) => s.openCopilot);
  const closeCopilot     = useUI((s) => s.closeCopilot);

  const [user, setUser] = useState<{ full_name: string; email: string } | null>(null);
  useEffect(() => {
    me()
      .then((u) => setUser({ full_name: u.full_name || u.email, email: u.email }))
      .catch(() => setUser(null));
  }, []);

  return (
    <div className="flex items-center gap-1 sm:gap-2 px-3 sm:px-6 lg:px-8 h-14 bg-canvas border-b border-hairline">
      <button
        onClick={toggleMobileNav}
        className="lg:hidden text-steel hover:text-ink p-1 rounded-md hover:bg-surface"
        aria-label="Open navigation"
      >
        <Menu className="size-5" />
      </button>

      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        {/* Mobile-only search trigger — the rail (and its search field) sits
         *  behind the drawer on phones, so keep a quick palette entry here. */}
        <button
          onClick={togglePalette}
          className="md:hidden text-steel hover:text-ink p-1 rounded-md hover:bg-surface"
          aria-label="Search"
        >
          <Search className="size-5" />
        </button>

        <button
          type="button"
          onClick={() => (copilotOpen || copilotMinimized) ? closeCopilot() : openCopilot()}
          aria-label="Logica AI Copilot"
          aria-pressed={copilotOpen || copilotMinimized}
          className={
            'inline-flex items-center justify-center size-8 rounded-full transition-colors ' +
            (copilotOpen || copilotMinimized
              ? 'bg-accent/15 text-accent'
              : 'text-steel hover:bg-surface hover:text-ink')
          }
          title="Logica AI Copilot"
        >
          <RiSparkling className="size-[18px]" />
        </button>

        <NotificationsPopover />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green/40">
              <Avatar name={user?.full_name} size="md" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[220px]">
            <DropdownMenuLabel>
              <div className="text-body-sm font-medium text-ink">{user?.full_name ?? 'Loading…'}</div>
              <div className="text-caption font-normal text-stone">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => void navigate({
                to: '/settings/$section' as never,
                params: { section: 'company' } as never,
              })}
            >
              Account settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => { await logout(); window.location.href = '/login'; }}
              className="text-brand-error data-[highlighted]:bg-brand-error/10 data-[highlighted]:text-brand-error"
            >
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CopilotPanel />
    </div>
  );
}
